import { useState, useMemo } from "react"
import {DollarSign, ChevronDown, RotateCcw, BarChart3, AlertTriangle, CheckCircle, Info, Scale, Percent, Layers} from 'lucide-react'

interface RegionPreset {
  name: string
  description: string
  costs: {
    hayForage: number
    grainSupplement: number
    mineral: number
    pastureLease: number
    vetHealth: number
    breeding: number
    labor: number
    equipDeprec: number
    insurance: number
    marketingHauling: number
    miscOverhead: number
  }
}

const REGION_PRESETS: RegionPreset[] = [
  {
    name: "Southeast US (Fescue Belt)",
    description: "Lower feed costs, longer grazing season",
    costs: { hayForage: 380, grainSupplement: 85, mineral: 35, pastureLease: 120, vetHealth: 42, breeding: 28, labor: 95, equipDeprec: 65, insurance: 18, marketingHauling: 32, miscOverhead: 45 },
  },
  {
    name: "Midwest / Corn Belt",
    description: "Moderate costs, good grain access",
    costs: { hayForage: 450, grainSupplement: 110, mineral: 38, pastureLease: 155, vetHealth: 45, breeding: 32, labor: 105, equipDeprec: 80, insurance: 22, marketingHauling: 35, miscOverhead: 50 },
  },
  {
    name: "Northern Plains / Mountain",
    description: "Higher feed costs, shorter grazing season",
    costs: { hayForage: 580, grainSupplement: 120, mineral: 40, pastureLease: 100, vetHealth: 38, breeding: 25, labor: 85, equipDeprec: 70, insurance: 20, marketingHauling: 40, miscOverhead: 48 },
  },
  {
    name: "Southwest / Arid",
    description: "High supplemental feed, limited pasture",
    costs: { hayForage: 650, grainSupplement: 145, mineral: 42, pastureLease: 90, vetHealth: 48, breeding: 30, labor: 90, equipDeprec: 75, insurance: 22, marketingHauling: 45, miscOverhead: 55 },
  },
  {
    name: "Custom Region",
    description: "Enter your own cost values",
    costs: { hayForage: 450, grainSupplement: 100, mineral: 38, pastureLease: 140, vetHealth: 42, breeding: 30, labor: 100, equipDeprec: 75, insurance: 20, marketingHauling: 35, miscOverhead: 50 },
  },
]

const COST_CATEGORIES = [
  { key: "hayForage", label: "Hay / Forage", hint: "Harvested hay, silage, and stored forage costs" },
  { key: "grainSupplement", label: "Grain / Supplement", hint: "Corn, distillers grains, protein supplements" },
  { key: "mineral", label: "Mineral & Salt", hint: "Mineral blocks, loose mineral, salt" },
  { key: "pastureLease", label: "Pasture / Land Lease", hint: "Lease payments, property tax, fencing" },
  { key: "vetHealth", label: "Vet & Health", hint: "Vaccinations, deworming, vet calls, treatments" },
  { key: "breeding", label: "Breeding / AI / Bull", hint: "AI costs, semen, bull lease or depreciation" },
  { key: "labor", label: "Labor", hint: "Hired labor or imputed owner labor value" },
  { key: "equipDeprec", label: "Equipment / Depreciation", hint: "Tractor, trailer, equipment wear and depreciation" },
  { key: "insurance", label: "Insurance", hint: "Livestock insurance, liability, property" },
  { key: "marketingHauling", label: "Marketing / Hauling", hint: "Sale barn fees, trucking, brand inspection" },
  { key: "miscOverhead", label: "Misc / Overhead", hint: "Fuel, utilities, accounting, supplies" },
] as const

type CostKey = typeof COST_CATEGORIES[number]["key"]

function getCostEfficiency(costPerLb: number): { label: string; color: string; bgColor: string; icon: "check" | "warning" | "alert"; message: string } {
  if (costPerLb <= 1.50) {
    return { label: "Very Efficient", color: "#2D6A4F", bgColor: "#B7E4C7", icon: "check", message: "Your cost of production is well below typical market prices. Strong competitive position for profitability." }
  }
  if (costPerLb <= 2.00) {
    return { label: "Efficient", color: "#40916C", bgColor: "#d1f0dd", icon: "check", message: "Good cost control. You have a healthy margin between production cost and typical market prices." }
  }
  if (costPerLb <= 2.50) {
    return { label: "Average", color: "#D4A373", bgColor: "#FEFAE0", icon: "warning", message: "Production costs are in the normal range. Look for opportunities to reduce your top 2-3 expense categories." }
  }
  if (costPerLb <= 3.00) {
    return { label: "Above Average", color: "#E76F51", bgColor: "#fce8e2", icon: "alert", message: "Costs are elevated. Focus on reducing feed expenses and improving calving rate to lower per-unit costs." }
  }
  return { label: "High Cost", color: "#c0392b", bgColor: "#fde2e2", icon: "alert", message: "Production costs are significantly above average. A thorough cost audit and operational restructuring is recommended." }
}

export default function CowCalfCostCalculator() {
  const [regionPreset, setRegionPreset] = useState(REGION_PRESETS[1])
  const [herdSize, setHerdSize] = useState("100")
  const [calvingRate, setCalvingRate] = useState("90")
  const [avgWeanWeight, setAvgWeanWeight] = useState("550")
  const [costs, setCosts] = useState<Record<CostKey, string>>(() => {
    const initial: Record<string, string> = {}
    COST_CATEGORIES.forEach((cat) => {
      initial[cat.key] = String(REGION_PRESETS[1].costs[cat.key as CostKey])
    })
    return initial as Record<CostKey, string>
  })
  const [showResults, setShowResults] = useState(false)

  const applyPreset = (preset: RegionPreset) => {
    setRegionPreset(preset)
    const newCosts: Record<string, string> = {}
    COST_CATEGORIES.forEach((cat) => {
      newCosts[cat.key] = String(preset.costs[cat.key as CostKey])
    })
    setCosts(newCosts as Record<CostKey, string>)
    setShowResults(false)
  }

  const updateCost = (key: CostKey, value: string) => {
    setCosts((prev) => ({ ...prev, [key]: value }))
    setShowResults(false)
  }

  const results = useMemo(() => {
    const h = parseFloat(herdSize)
    const cr = parseFloat(calvingRate) / 100
    const ww = parseFloat(avgWeanWeight)

    if (isNaN(h) || h <= 0 || isNaN(cr) || cr <= 0 || isNaN(ww) || ww <= 0) return null

    const parsedCosts: Record<CostKey, number> = {} as Record<CostKey, number>
    let totalCostPerCow = 0
    for (const cat of COST_CATEGORIES) {
      const val = parseFloat(costs[cat.key])
      if (isNaN(val) || val < 0) return null
      parsedCosts[cat.key] = val
      totalCostPerCow += val
    }

    const totalHerdCost = totalCostPerCow * h
    const calvesWeaned = Math.floor(h * cr)
    const costPerCalfWeaned = calvesWeaned > 0 ? totalHerdCost / calvesWeaned : 0
    const totalLbsProduced = calvesWeaned * ww
    const costPerLb = totalLbsProduced > 0 ? totalHerdCost / totalLbsProduced : 0

    // Build sorted expense breakdown
    const breakdown = COST_CATEGORIES.map((cat) => ({
      key: cat.key,
      label: cat.label,
      value: parsedCosts[cat.key],
      percent: totalCostPerCow > 0 ? (parsedCosts[cat.key] / totalCostPerCow) * 100 : 0,
    })).sort((a, b) => b.value - a.value)

    const COLORS = ["#2D6A4F", "#40916C", "#606C38", "#D4A373", "#E76F51", "#283618", "#B7E4C7", "#8fbc8f", "#c4935f", "#a0522d", "#6b8e23"]

    const breakdownWithColors = breakdown.map((item, i) => ({
      ...item,
      color: COLORS[i % COLORS.length],
    }))

    // Feed total (hay + grain + mineral) as % of total
    const feedTotal = parsedCosts.hayForage + parsedCosts.grainSupplement + parsedCosts.mineral
    const feedPercent = totalCostPerCow > 0 ? (feedTotal / totalCostPerCow) * 100 : 0

    const efficiency = getCostEfficiency(costPerLb)

    return {
      totalCostPerCow,
      totalHerdCost,
      calvesWeaned,
      costPerCalfWeaned,
      totalLbsProduced,
      costPerLb,
      breakdown: breakdownWithColors,
      feedTotal,
      feedPercent,
      efficiency,
    }
  }, [herdSize, calvingRate, avgWeanWeight, costs])

  const handleCalculate = () => {
    if (parseFloat(herdSize) > 0) setShowResults(true)
  }

  const handleReset = () => {
    applyPreset(REGION_PRESETS[1])
    setHerdSize("100")
    setCalvingRate("90")
    setAvgWeanWeight("550")
    setShowResults(false)
  }

  const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 })
  const fmtDec = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return (
    <section id="cow-calf-cost-calculator" className="py-12 sm:py-16 lg:py-20 bg-[#FEFAE0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Production Cost Analysis
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            Cow Calf Cost of Production
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-xl mx-auto">
            Break down your total cost of production per calf including feed, health, breeding, labor, and overhead to identify where your dollars go.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-[#2D6A4F]/8 border border-[#B7E4C7]/40 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#606C38] to-[#283618] px-6 sm:px-8 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Layers className="w-5 h-5 text-[#FEFAE0]" />
              </div>
              <div>
                <h3 className="text-[#FEFAE0] font-bold text-lg" style={{ fontFamily: "'Merriweather', serif" }}>
                  Cost of Production Calculator
                </h3>
                <p className="text-[#B7E4C7] text-sm">
                  Detailed expense breakdown per cow &amp; per calf
                </p>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div className="p-6 sm:p-8">
            {/* Region Preset */}
            <div className="mb-6">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                <BarChart3 className="w-3.5 h-3.5 text-[#40916C]" />
                Regional Cost Preset
              </label>
              <div className="relative">
                <select
                  value={regionPreset.name}
                  onChange={(e) => {
                    const rp = REGION_PRESETS.find((r) => r.name === e.target.value)
                    if (rp) applyPreset(rp)
                  }}
                  className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all appearance-none cursor-pointer pr-10"
                >
                  {REGION_PRESETS.map((rp) => (
                    <option key={rp.name} value={rp.name}>{rp.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#606C38] pointer-events-none" />
              </div>
              <p className="mt-1.5 text-xs text-[#606C38]">{regionPreset.description}</p>
            </div>

            {/* Herd Info */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-[#2D6A4F] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Scale className="w-4 h-4" />
                Herd Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                    <Scale className="w-3.5 h-3.5 text-[#40916C]" />
                    Herd Size (cows)
                  </label>
                  <input
                    type="number"
                    value={herdSize}
                    onChange={(e) => { setHerdSize(e.target.value); setShowResults(false) }}
                    placeholder="e.g. 100"
                    min="1"
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40"
                  />
                  <p className="mt-1.5 text-xs text-[#606C38]">Total breeding cows</p>
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                    <Percent className="w-3.5 h-3.5 text-[#40916C]" />
                    Calving Rate (%)
                  </label>
                  <input
                    type="number"
                    value={calvingRate}
                    onChange={(e) => { setCalvingRate(e.target.value); setShowResults(false) }}
                    placeholder="e.g. 90"
                    min="1"
                    max="100"
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40"
                  />
                  <p className="mt-1.5 text-xs text-[#606C38]">Percent of cows weaning a calf</p>
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                    <Scale className="w-3.5 h-3.5 text-[#40916C]" />
                    Avg Wean Weight (lbs)
                  </label>
                  <input
                    type="number"
                    value={avgWeanWeight}
                    onChange={(e) => { setAvgWeanWeight(e.target.value); setShowResults(false) }}
                    placeholder="e.g. 550"
                    min="1"
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40"
                  />
                  <p className="mt-1.5 text-xs text-[#606C38]">Average calf weight at weaning</p>
                </div>
              </div>
            </div>

            {/* Cost Inputs */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-[#E76F51] uppercase tracking-wider mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Annual Cost Per Cow
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {COST_CATEGORIES.map((cat) => (
                  <div key={cat.key}>
                    <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                      <DollarSign className="w-3.5 h-3.5 text-[#40916C]" />
                      {cat.label} ($/cow)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#606C38] font-medium text-sm">$</span>
                      <input
                        type="number"
                        value={costs[cat.key]}
                        onChange={(e) => updateCost(cat.key, e.target.value)}
                        placeholder="0"
                        min="0"
                        className="w-full pl-8 pr-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40"
                      />
                    </div>
                    <p className="mt-1.5 text-xs text-[#606C38]">{cat.hint}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCalculate}
                disabled={!herdSize || parseFloat(herdSize) <= 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#245a42] disabled:bg-[#B7E4C7] disabled:cursor-not-allowed text-[#FEFAE0] font-bold text-base sm:text-lg px-6 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0"
              >
                <BarChart3 className="w-5 h-5" />
                Analyze Costs
              </button>
              {showResults ? (
                <button
                  onClick={handleReset}
                  className="inline-flex items-center justify-center gap-2 bg-[#FEFAE0] hover:bg-[#f0ead2] text-[#606C38] font-semibold px-5 py-4 rounded-xl border-2 border-[#B7E4C7] transition-all duration-200"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              ) : null}
            </div>
          </div>

          {/* Results Section */}
          {showResults && results ? (
            <div className="border-t-2 border-[#B7E4C7]/40 bg-gradient-to-b from-[#FEFAE0]/50 to-white p-6 sm:p-8 animate-fade-in-up">
              {/* Main Result */}
              <div className="text-center mb-6 pb-6 border-b border-[#B7E4C7]/30">
                <p className="text-sm font-semibold text-[#40916C] uppercase tracking-wider mb-1">
                  Cost Per Calf Weaned
                </p>
                <p className="text-4xl sm:text-5xl font-bold text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                  {fmt(results.costPerCalfWeaned)}
                </p>
                <p className="mt-2 text-sm text-[#606C38]">
                  {herdSize} cows &bull; {results.calvesWeaned} calves weaned &bull; {results.totalLbsProduced.toLocaleString()} lbs produced
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-[#2D6A4F]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Cost / Cow</span>
                  </div>
                  <p className="text-2xl font-bold text-[#283618]">{fmt(results.totalCostPerCow)}</p>
                  <p className="text-xs text-[#606C38] mt-1">annual per cow</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-[#D4A373]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Cost / Lb</span>
                  </div>
                  <p className="text-2xl font-bold text-[#283618]">{fmtDec(results.costPerLb)}</p>
                  <p className="text-xs text-[#606C38] mt-1">per lb of calf produced</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-[#E76F51]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Total Herd Cost</span>
                  </div>
                  <p className="text-2xl font-bold text-[#283618]">{fmt(results.totalHerdCost)}</p>
                  <p className="text-xs text-[#606C38] mt-1">entire operation</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="w-4 h-4 text-[#606C38]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Feed %</span>
                  </div>
                  <p className="text-2xl font-bold text-[#283618]">{results.feedPercent.toFixed(1)}%</p>
                  <p className="text-xs text-[#606C38] mt-1">of total costs ({fmt(results.feedTotal)})</p>
                </div>
              </div>

              {/* Expense Breakdown Stacked Bar */}
              <div className="bg-[#FEFAE0] rounded-xl p-4 sm:p-5 border border-[#B7E4C7]/30 mb-6">
                <h4 className="font-bold text-sm text-[#283618] mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#40916C]" />
                  Cost Breakdown (Per Cow) — Ranked by Impact
                </h4>
                {/* Stacked bar */}
                <div className="h-6 rounded-full overflow-hidden flex mb-4">
                  {results.breakdown.map((item) => (
                    <div
                      key={item.key}
                      className="h-full transition-all duration-500"
                      style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                      title={`${item.label}: ${fmt(item.value)} (${item.percent.toFixed(1)}%)`}
                    />
                  ))}
                </div>

                {/* Ranked list */}
                <div className="space-y-2">
                  {results.breakdown.map((item, index) => (
                    <div key={item.key} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-[#606C38] w-5 text-right">{index + 1}.</span>
                      <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-[#283618] flex-1">{item.label}</span>
                      <span className="text-sm font-bold text-[#283618]">{fmt(item.value)}</span>
                      <span className="text-xs text-[#606C38] w-12 text-right">{item.percent.toFixed(1)}%</span>
                      {/* Mini bar */}
                      <div className="w-20 h-2 bg-[#B7E4C7]/30 rounded-full overflow-hidden hidden sm:block">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${item.percent}%`, backgroundColor: item.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Insight */}
              <div className="bg-white rounded-xl p-4 sm:p-5 border border-[#B7E4C7]/30 mb-6">
                <h4 className="font-bold text-sm text-[#283618] mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#D4A373]" />
                  Key Insight
                </h4>
                <p className="text-sm text-[#606C38] leading-relaxed">
                  Your top 3 expense categories ({results.breakdown.slice(0, 3).map((b) => b.label).join(", ")}) account for{" "}
                  <strong className="text-[#283618]">
                    {results.breakdown.slice(0, 3).reduce((sum, b) => sum + b.percent, 0).toFixed(1)}%
                  </strong>{" "}
                  of your total cost of production. Reducing these by even 10% would save{" "}
                  <strong className="text-[#2D6A4F]">
                    {fmt(results.breakdown.slice(0, 3).reduce((sum, b) => sum + b.value, 0) * 0.1)}/cow
                  </strong>{" "}
                  or{" "}
                  <strong className="text-[#2D6A4F]">
                    {fmt(results.breakdown.slice(0, 3).reduce((sum, b) => sum + b.value, 0) * 0.1 * parseFloat(herdSize))}
                  </strong>{" "}
                  across your entire herd.
                </p>
              </div>

              {/* Efficiency Assessment */}
              <div
                className="rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 border"
                style={{ backgroundColor: results.efficiency.bgColor, borderColor: results.efficiency.color + "30" }}
              >
                <div className="shrink-0 mt-0.5">
                  {results.efficiency.icon === "check" ? (
                    <CheckCircle className="w-5 h-5" style={{ color: results.efficiency.color }} />
                  ) : results.efficiency.icon === "warning" ? (
                    <AlertTriangle className="w-5 h-5" style={{ color: results.efficiency.color }} />
                  ) : (
                    <AlertTriangle className="w-5 h-5" style={{ color: results.efficiency.color }} />
                  )}
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: results.efficiency.color }}>
                    Cost Efficiency: {results.efficiency.label} ({fmtDec(results.costPerLb)}/lb)
                  </p>
                  <p className="text-sm text-[#283618]/80 mt-1 leading-relaxed">
                    {results.efficiency.message}
                  </p>
                </div>
              </div>

              <p className="mt-5 text-xs text-[#606C38]/70 text-center leading-relaxed">
                Cost estimates are based on regional averages and may vary significantly with local conditions. Consult your extension agent, agricultural lender, or farm management specialist for a detailed enterprise budget.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
